/**
 * Screen Wake Lock API wrapper — prevents the phone from auto-locking while
 * Read Aloud Mode is playing. Crucial for Commute Mode because Android Chrome
 * pauses SpeechSynthesis when the screen sleeps.
 *
 * Behavior:
 *   - request()  → acquires a "screen" wake lock; returns true on success
 *   - release()  → releases it
 *   - Auto re-acquires when the tab becomes visible again (Wake Lock is
 *     automatically released by the browser on tab background — well-documented
 *     spec behavior; React caller must call `request()` again on visibilitychange)
 */

import { useEffect, useRef, useState } from "react";

export function isWakeLockSupported() {
  return typeof navigator !== "undefined" && "wakeLock" in navigator;
}

/**
 * React hook — call with `active=true` whenever you want the screen to stay on.
 * Returns:
 *   { supported: boolean, locked: boolean, error: string | null }
 */
export function useWakeLock(active) {
  const sentinelRef = useRef(null);
  const [locked, setLocked] = useState(false);
  const [error, setError] = useState(null);
  const supported = isWakeLockSupported();

  useEffect(() => {
    if (!supported) return undefined;
    let cancelled = false;

    const acquire = async () => {
      if (!active || document.visibilityState !== "visible") return;
      try {
        const s = await navigator.wakeLock.request("screen");
        if (cancelled) { try { await s.release(); } catch { /* noop */ } return; }
        sentinelRef.current = s;
        setLocked(true);
        setError(null);
        s.addEventListener("release", () => {
          // The browser may release the lock at any time. Reflect that in state.
          if (sentinelRef.current === s) {
            sentinelRef.current = null;
            setLocked(false);
          }
        });
      } catch (e) {
        setError(e?.message || "Wake Lock denied");
        setLocked(false);
      }
    };

    const release = async () => {
      const s = sentinelRef.current;
      sentinelRef.current = null;
      setLocked(false);
      if (s) { try { await s.release(); } catch { /* noop */ } }
    };

    const onVis = () => {
      if (document.visibilityState === "visible" && active) acquire();
    };
    document.addEventListener("visibilitychange", onVis);

    if (active) acquire();
    else release();

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVis);
      release();
    };
  }, [active, supported]);

  return { supported, locked, error };
}
